import React, {
  useState, useEffect, useMemo, useCallback,
} from "react";

import Highlighter from "react-highlight-words";

import {
  INVALID_REPORT_SPEC_FLAGS,
  SPEC_FLAGS_MAP,
} from "../../../Common/constants/SpecFlagConstants";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import LockedValue from "../../../grace-period/components/LockedValue";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import { isResultRevealingSpec } from "../../../grace-period/utils";
import { resolveDisplaySampleId } from "../../../Product/Report/Components/ReportSpecs/helper";
import checkStatusEditable, { sampleHasParentSample } from "../helper";
import ActionsMenu from "./ActionsMenu";
import "./SampleCard.css";

import { getFileFromS3WithPresigned } from "../../../../utils/helpers";
import { PRODUCT_ENVIRONMENT_CATEGORY } from "../../utils";

import { ReactComponent as EnvironmentIcon } from "../../../../assets/images/feature-icons/EnvironmentIconFilled.svg";
import { ReactComponent as ProductIcon } from "../../../../assets/images/feature-icons/ProductIconFilled.svg";

function buildSampleSummaryPreview(sample) {
  const displaySampleId = resolveDisplaySampleId(sample);
  if (!displaySampleId) {
    return "";
  }
  return `Sample ID: ${displaySampleId}`;
}

export default function SampleCard(props) {
  const {
    sample,
    sampleFields,
    sampleFieldsInfo,
    handleEditSample,
    handleDeleteSample,
    submissionStatus,
    sampleCategory,
    submissionSampleCategory,
    handleViewReportData,
    submissionEventId,
    highlightSearchWords = [],
  } = props;
  const [showTestView, setShowTestView] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const { areResultsLocked } = useAccessStatus();

  const invalidReportSpecFlags = INVALID_REPORT_SPEC_FLAGS;
  /** Toggle test view.  */ const handleClickTestViewSwitch = () => {
    setShowTestView(!showTestView);
  };

  const disableActionsForSubmission = !checkStatusEditable(submissionStatus, null, submissionEventId);
  const disableActionsForSample = !checkStatusEditable(submissionStatus, sample.specs_flag);
  const isRetestSample = sampleHasParentSample(sample);

  /**
   * Load image from AWS
   * @param {String} image_path path to image file in aws
   */
  const loadImageFromAWS = async (image_path) => {
    setLoadingImage(true);
    if (image_path) {
      const fileBlobObj = await getFileFromS3WithPresigned([image_path], "private");
      const imageBlob = fileBlobObj[image_path];
      if (imageBlob) {
        setImageSrc(window.URL.createObjectURL(imageBlob));
      }
    }
    setLoadingImage(false);
  };

  useEffect(() => {
    if (sample.image_path) {
      loadImageFromAWS(sample.image_path);
    }
  }, []); // eslint-disable-line

  const summaryPreviewText = useMemo(
    () => buildSampleSummaryPreview(sample),
    [sample],
  );
  const displaySampleId = resolveDisplaySampleId(sample);

  // const renderEditedSampleIdSuffix = () => {
  //   if (!hasEditedSampleId) {
  //     return null;
  //   }
  //   return (
  //     <button
  //       type="button"
  //       className="SampleCard__EditedSuffix"
  //       aria-label="View Sample ID change history"
  //     >
  //       {"\u00A0(Edited)"}
  //     </button>
  //   );
  // };

  const renderHighlighted = useCallback((raw) => {
    const text = raw === null || raw === undefined ? "" : String(raw);
    if (!highlightSearchWords.length || text === "") {
      return text || "";
    }
    return (
      <Highlighter
        highlightClassName="searchHighlight"
        searchWords={highlightSearchWords}
        autoEscape
        textToHighlight={text}
      />
    );
  }, [highlightSearchWords]);

  const renderSampleStatus = () => {
    const spec = SPEC_FLAGS_MAP[sample.specs_flag];
    if (!spec) return null;
    const statusContent = (
      <div className="sample-card-status">
        <img src={spec.icon} alt="" />
        <span style={{ color: spec.color }}>
          {spec.label}
        </span>
      </div>
    );
    if (areResultsLocked && isResultRevealingSpec(sample.specs_flag)) {
      return (
        <div className="sample-card-status">
          <LockedValue locked />
        </div>
      );
    }
    if (String(sample.specs_flag) === "-4") {
      return (
        <StyledTooltip
          title="Until setup is complete, raw results will be shared by email once testing is complete."
          placement="top"
        >
          {statusContent}
        </StyledTooltip>
      );
    }
    return statusContent;
  };

  const renderTestsBadge = (clickable) => {
    const testsCount = sample?.tests_list?.length ?? 0;
    const label = showTestView
      ? "Sample Details"
      : `${testsCount} ${testsCount === 1 ? "Test" : "Tests"}`;
    const content = renderHighlighted(label);

    if (!clickable) {
      return (
        <div className="added-sample-tests-number added-sample-tests-number--readonly">
          <div>{content}</div>
        </div>
      );
    }

    return (
      <div
        className="added-sample-tests-number"
        onClick={(e) => {
          e.stopPropagation();
          handleClickTestViewSwitch();
        }}
      >
        <div>{content}</div>
      </div>
    );
  };

  const renderStatusAndTestsRow = (clickableTests) => (
    <div className="sample-card-status-test-view-cointainer">
      <div className="SampleCard__ActionsStatusContainer">
        {renderSampleStatus()}
      </div>
      <div className="sample-card-test-view-choice">
        {renderTestsBadge(clickableTests)}
      </div>
    </div>
  );

  const showSampleCategoryIcon = submissionSampleCategory === PRODUCT_ENVIRONMENT_CATEGORY;

  const renderSampleCategoryIcon = () => {
    if (!showSampleCategoryIcon) return null;
    if (sample.sample_category === "product") {
      return <ProductIcon className="SampleCard__CategoryIconSvg" aria-label="Product sample" />;
    }
    if (sample.sample_category === "environment") {
      return <EnvironmentIcon className="SampleCard__CategoryIconSvg" aria-label="Environment sample" />;
    }
    return null;
  };

  return (
    <>
      <div
        className={`sample-card sample-card-modal sample-card-details-modal sample-card-details-sampleid-container${showTestView ? " sample-card--test-view" : ""}${invalidReportSpecFlags.includes(sample.specs_flag) ? " sample-card-details-sampleid-container-disabled" : ""}`}
        style={{ paddingRight: !disableActionsForSubmission ? "16px" : "10px" }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!invalidReportSpecFlags.includes(sample.specs_flag)) {
            handleViewReportData();
          }
        }}
        onMouseLeave={() => {
          if (showTestView) {
            setShowTestView(false);
          }
        }}
      >
        <div className="SampleCard__HeaderActions">
          {renderSampleCategoryIcon()}
          {!disableActionsForSubmission && (
            <ActionsMenu
              handleEdit={handleEditSample}
              disableEditRetest={isRetestSample}
              handleDelete={handleDeleteSample}
              disableDelete={parseInt(submissionStatus) === 0}
              disabled={disableActionsForSample}
              disabledTooltipText="Cannot modify sample at this stage"
              sampleCategory={sampleCategory}
            />
          )}
        </div>
        {showTestView ? (
          <div className="sample-card-test-view-body">
            <StyledScrollbar autoHide>
              <div className="sample-card-details sample-test-ul-container">
                <ul className="sample-test-ul">
                  {sample.tests_list.map((test, idx) => (
                    <li key={idx}>{test.Test}</li>
                  ))}
                </ul>
              </div>
            </StyledScrollbar>
          </div>
        ) : (
          <>
            {(sample.num_composite_samples > 0) && (
              <div
                className="sample-card-composite-header"
                style={{
                  width: !disableActionsForSubmission ? "calc(100% + 28px)" : "calc(100% + 22px)",
                }}
              >
                <div>Composite Sample</div>
                <div>{`${sample.num_composite_samples} Sub Samples`}</div>
              </div>
            )}
            <div className="sample-card-hover-stack">
              <div className="sample-card-summary-preview">
                {renderHighlighted(summaryPreviewText)}
                {/* {renderEditedSampleIdSuffix()} */}
              </div>
              <div className="sample-card-summary-footer">
                {renderStatusAndTestsRow(false)}
              </div>
              <div className="sample-card-detail-overlay">
                <div className="sample-card-detail-overlay__scroll">
                  <div className="sample-card-details">
                    <table>
                      <tbody>
                        <tr>
                          <th>Sample ID</th>
                          <td>
                            {renderHighlighted(displaySampleId)}
                            {/* {renderEditedSampleIdSuffix()} */}
                          </td>
                        </tr>
                        {sampleFields.map((json_field, i) => {
                          if (json_field === "sample_id") return null;
                          const fieldValue = sample[json_field];
                          if (fieldValue === "" || fieldValue === undefined) return null;
                          return (
                            <tr key={`${json_field}${i}`}>
                              <th>{sampleFieldsInfo[json_field].title_field}</th>
                              <td>{renderHighlighted(fieldValue)}</td>
                            </tr>
                          );
                        })}
                        {(imageSrc !== "" || loadingImage) && (
                          <tr key="image">
                            <th>Image</th>
                            <td>
                              <div className="SampleSubmission__SampleCardImageDiv">
                                {loadingImage ? (
                                  <div className="SampleSubmission__SampleCardLoading" />
                                ) : (
                                  <img
                                    src={imageSrc}
                                    alt="sample"
                                    className="SampleSubmission__SampleCardImage"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {renderStatusAndTestsRow(true)}
              </div>
            </div>
          </>
        )}

        {showTestView && renderStatusAndTestsRow(true)}
      </div>
    </>
  );
}
